import { Formik } from 'formik'
import { useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import * as Yup from 'yup'
import { Button } from '@/components/button'
import { Row, Group, PasswordInput, Form } from '@/components/form'
import { colors, fontWeights } from '@/styles'
import handleSession from '@/utils/handle-session'
import OrDiv from './or-div'
import TermsAndPrivacyPolicyDiv from './terms-and-privacy-div'
import HeaderDiv from './header-div'
import BackButton from './back-button'
import EmailFieldInput from './email-fieldinput'
import Context from '../context'

const PasswordContainer = styled.div`
  position: relative;
`

const PasswordGroup = styled(Group)`
  margin: 0 0 6px;
`

const LoginForm = styled(Form)`
  width: 100%;
`

const ButtonContainer = styled(Group)`
  text-align: center;
  margin: 10px auto 16px;
`

const CenterRow = styled(Row)`
  justify-content: center;
`

const ForgotRow = styled.div`
  display: flex;
  justify-content: flex-start;
`

const ForgotPasswordButton = styled(Button)`
  font-size: 14px;
  color: ${colors.link} !important;
  font-weight: ${fontWeights.medium} !important;
  text-align: left;
  min-width: unset;
  min-height: unset;
  line-height: 16px;
  margin: 0 0 16px;
  padding: 0 !important;
  cursor: pointer;
`

const LoginButtonContainer = styled(ButtonContainer)`
  margin-top: 0px;
  margin-bottom: 0px;
`

const AutoLoginButtonContainer = styled(LoginButtonContainer)`
  margin: 0 auto;
`

const MarginSpacer = styled.div`
  margin-bottom: 16px;
`

const PrimaryButton = styled(Button)`
  font-size: 16px;
  font-weight: ${fontWeights.bold};
`

const AutoLoginButton = styled(Button)`
  font-size: 16px;
  font-weight: ${fontWeights.bold};
  color: ${colors.blue16} !important;
  background-color: ${colors.white} !important;
  border: 1.5px solid ${colors.grey88} !important;
  box-shadow: unset !important;
`

const Password = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [lastButtonPressed, setLastButton] = useState('autologin')
  const [autoLoginSubmitting, setAutoLoginSubmitting] = useState(false)
  const { username, setEmail, setCurrentStep } = useContext(Context)

  const showError = () => setLastButton('autologin')

  const autoLogin = () => async () => {
    setAutoLoginSubmitting(true)
    const { email } = await dispatch.auth.requestAutoLogin({ username })

    setEmail(email)
    setCurrentStep('checkEmail')
    setAutoLoginSubmitting(false)
  }

  const onSubmit = async (values, { setSubmitting }) => {
    const { error } = await handleSession(
      dispatch.auth.login({
        ...values
      })
    )
    if (error) setLastButton('login')
    setSubmitting(false)
  }

  const validationSchema = Yup.object().shape({
    username: Yup.string().phoneEmail(),
    password: Yup.string().required()
  })

  return (
    <>
      <BackButton onClick={() => setCurrentStep('login')} />
      <HeaderDiv
        headingText={t('b.landing.view.welcome_back_login.text', 'Welcome back! Log in below.')}
        hasSignUpHereLink
      />
      <PasswordContainer>
        <Formik
          initialValues={{ username: username || '', password: '' }}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ isSubmitting, values, errors }) => (
            <LoginForm noValidate>
              <EmailFieldInput />
              <MarginSpacer />
              <Row>
                <PasswordGroup col={12}>
                  <PasswordInput
                    name="password"
                    autoFocus
                    placeholder={t('b.auth.view.password.label', 'Password')}
                    required
                    hideError={lastButtonPressed === 'autologin'}
                  />
                </PasswordGroup>
              </Row>
              <ForgotRow>
                <ForgotPasswordButton
                  id="web-password-forgotpassword-button"
                  theme="text"
                  onClick={() => setCurrentStep('forgotPassword')}
                  data-testid="reset-password"
                >
                  {t('b.auth.view.forgot_password.link', 'Forgot Password?')}
                </ForgotPasswordButton>
              </ForgotRow>
              <Row>
                <LoginButtonContainer col={12}>
                  <PrimaryButton
                    id="web-LoginSocial-submit-button"
                    theme="primary"
                    type="submit"
                    width="100%"
                    radius={6}
                    disabled={isSubmitting}
                    shadow
                  >
                    {t('b.auth.view.login.button', 'Log In')}
                  </PrimaryButton>
                </LoginButtonContainer>
              </Row>
              <OrDiv orText={t('b.auth.view.login_or.text', 'OR')} />
              <CenterRow>
                <AutoLoginButtonContainer col={12}>
                  <AutoLoginButton
                    id="web-autologin-submit-button"
                    theme="default"
                    width="100%"
                    radius={6}
                    shadow
                    onClick={!errors['username'] ? autoLogin(values) : showError}
                    disabled={isSubmitting || autoLoginSubmitting}
                  >
                    {t('b.auth.view.email_auto_login_link.button', 'Email me an auto-login link')}
                  </AutoLoginButton>
                </AutoLoginButtonContainer>
              </CenterRow>
              <TermsAndPrivacyPolicyDiv />
            </LoginForm>
          )}
        </Formik>
      </PasswordContainer>
    </>
  )
}

export default Password
